import { useEffect } from "react";
import type { GossipItem } from "@/mocks/gossip";

interface Props {
  item: GossipItem | null;
  related: GossipItem[];
  onClose: () => void;
  onPick: (item: GossipItem) => void;
}

function toWan(n: number) {
  return n >= 10000 ? `${(n / 10000).toFixed(1)}w` : `${n}`;
}

export default function ArticleModal({ item, related, onClose, onPick }: Props) {
  useEffect(() => {
    if (!item) return;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [item, onClose]);

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6" role="dialog" aria-modal="true" aria-label={item.title}>
      <div className="absolute inset-0 bg-foreground-950/70 backdrop-blur-sm" onClick={onClose} aria-hidden="true"></div>

      <div className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-background-50">
        <button
          type="button"
          onClick={onClose}
          aria-label="关闭阅读窗口"
          className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full bg-background-50/90 text-foreground-700 transition-colors hover:bg-foreground-950 hover:text-background-50"
        >
          <i className="ri-close-line text-xl leading-none" aria-hidden="true"></i>
        </button>

        <div className="overflow-y-auto">
          <div className="relative">
            <img src={item.cover} alt={item.title} className="h-52 w-full object-cover object-top md:h-72" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground-950/80 to-transparent p-5 pt-12">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-[11px] font-semibold text-background-50">
                <i className="ri-fire-fill text-accent-300" aria-hidden="true"></i>
                {item.tag} · {item.category}
              </span>
            </div>
          </div>

          <article className="px-5 pb-8 pt-5 md:px-8">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5 font-medium text-primary-700">
                <i className="ri-user-voice-fill" aria-hidden="true"></i>
                采编 {item.author}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line" aria-hidden="true"></i>
                {item.time}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-eye-line" aria-hidden="true"></i>
                {toWan(item.views)} 阅读
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-heart-3-fill text-primary-500" aria-hidden="true"></i>
                {toWan(item.likes)} 收藏
              </span>
              <span className="ml-auto inline-flex items-center gap-1 text-secondary-600">
                <i className="ri-book-read-line" aria-hidden="true"></i>
                约 {item.read} 读完
              </span>
            </div>

            <h2 className="mt-3 text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">{item.title}</h2>
            <p className="mt-3 text-base font-medium leading-relaxed text-primary-700/80">{item.summary}</p>

            <div className="my-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-background-200" aria-hidden="true"></span>
              <span className="grid h-8 w-8 place-items-center rounded-full bg-primary-500 text-background-50">
                <i className="ri-flashlight-fill text-sm leading-none" aria-hidden="true"></i>
              </span>
              <span className="h-px flex-1 bg-background-200" aria-hidden="true"></span>
            </div>

            <div className="space-y-5">
              {item.body.map((p, i) => (
                <p key={i} className="text-[15px] leading-8 text-foreground-800">
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-6 rounded-2xl bg-background-100 px-5 py-4 text-sm text-foreground-600">
              <i className="ri-information-line mr-1 text-primary-600" aria-hidden="true"></i>
              提示：本文依据多方公开信息整理，事件后续进展以相关方正式回应为准。爆料网坚持来源标注，涉及「传闻」的内容仅供参考。
            </div>
          </article>

          <div className="border-t border-background-200 px-5 py-6 md:px-8">
            <h3 className="flex items-center gap-2 text-base font-semibold text-foreground-950">
              <i className="ri-link-m" aria-hidden="true"></i>
              继续吃瓜 · 相关推荐
            </h3>
            <ul className="mt-4 space-y-3">
              {related.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => onPick(r)}
                    className="group flex items-center justify-between gap-4 rounded-xl bg-background-100 px-4 py-3 text-left transition-colors hover:bg-accent-100"
                  >
                    <span className="text-sm font-medium text-foreground-800 transition-colors group-hover:text-foreground-950">
                      {r.title}
                    </span>
                    <i className="ri-arrow-right-s-line shrink-0 text-lg text-primary-500" aria-hidden="true"></i>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}